import { useState } from 'react'
import { useAuthStore } from '../store'
import {
  Settings, User, Lock, Bell, Database, Save, Shield, Eye, EyeOff,
  Mail, Phone, Building2, BadgeCheck, Camera, Trash2, Download,
  Moon, Sun, Globe, Clock, CheckCircle, AlertTriangle, RefreshCw
} from 'lucide-react'
import toast from 'react-hot-toast'

// Toggle Switch component
const Toggle = ({ checked, onChange, disabled = false }) => (
  <label className={`relative inline-flex items-center ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => !disabled && onChange(e.target.checked)}
      className="sr-only peer"
      disabled={disabled}
    />
    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-emerald-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all after:shadow-sm"></div>
  </label>
)

// Setting Item component
const SettingItem = ({ icon: Icon, title, description, children, danger = false }) => (
  <div className={`flex items-start justify-between p-4 rounded-xl border transition-colors ${
    danger ? 'border-red-100 hover:bg-red-50' : 'border-gray-100 hover:bg-gray-50'
  }`}>
    <div className="flex items-start gap-4">
      <div className={`p-2 rounded-lg ${danger ? 'bg-red-100' : 'bg-gray-100'}`}>
        <Icon className={`w-5 h-5 ${danger ? 'text-red-600' : 'text-gray-600'}`} />
      </div>
      <div>
        <p className={`font-medium ${danger ? 'text-red-700' : 'text-gray-800'}`}>{title}</p>
        <p className="text-sm text-gray-500 mt-0.5">{description}</p>
      </div>
    </div>
    <div className="flex-shrink-0 ml-4">
      {children}
    </div>
  </div>
)

// Input Field component
const InputField = ({ label, type = 'text', value, onChange, placeholder, icon: Icon, disabled = false }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
      )}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all disabled:bg-gray-50 disabled:text-gray-500`}
      />
    </div>
  </div>
)

export default function Configuracion() {
  const user = useAuthStore(state => state.user)
  const [activeTab, setActiveTab] = useState('perfil')
  const [loading, setLoading] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)

  const [perfil, setPerfil] = useState({
    nombre: user?.nombre || 'Administrador',
    email: user?.email || 'admin@imss.gob.mx',
    telefono: user?.telefono || '55-1234-5678',
    hospital: user?.hospital || 'HGZ No. 53',
    rol: user?.rol || 'Administrador',
  })

  const [password, setPassword] = useState({
    current: '',
    new: '',
    confirm: '',
  })

  const [notificaciones, setNotificaciones] = useState({
    email: true,
    push: true,
    urgentes: true,
    diferimientos: true,
    ocupacion: true,
    reportes: false,
  })

  const [preferencias, setPreferencias] = useState({
    tema: 'light',
    idioma: 'es',
    zonaHoraria: 'America/Mexico_City',
    formatoFecha: 'DD/MM/YYYY',
  })

  const handleSavePerfil = async () => {
    setLoading(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 1000))
      toast.success('Perfil actualizado correctamente')
    } catch (error) {
      toast.error('Error al actualizar perfil')
    } finally {
      setLoading(false)
    }
  }

  const handleChangePassword = async () => {
    if (!password.current) {
      toast.error('Ingrese su contrasena actual')
      return
    }
    if (password.new.length < 8) {
      toast.error('La nueva contrasena debe tener al menos 8 caracteres')
      return
    }
    if (password.new !== password.confirm) {
      toast.error('Las contrasenas no coinciden')
      return
    }
    setLoading(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 1000))
      toast.success('Contrasena actualizada correctamente')
      setPassword({ current: '', new: '', confirm: '' })
    } catch (error) {
      toast.error('Error al cambiar contrasena')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveNotificaciones = async () => {
    setLoading(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 500))
      toast.success('Preferencias de notificaciones guardadas')
    } catch (error) {
      toast.error('Error al guardar preferencias')
    } finally {
      setLoading(false)
    }
  }

  const handleExportData = () => {
    toast.success('Exportando datos... El archivo se descargara en breve')
  }

  const handleDeleteAccount = () => {
    toast.error('Esta accion requiere confirmacion adicional')
  }

  const tabs = [
    { id: 'perfil', label: 'Perfil', icon: User },
    { id: 'seguridad', label: 'Seguridad', icon: Shield },
    { id: 'notificaciones', label: 'Notificaciones', icon: Bell },
    { id: 'preferencias', label: 'Preferencias', icon: Settings },
  ]

  const getPasswordStrength = (pass) => {
    if (!pass) return { level: 0, text: '', color: '' }
    let strength = 0
    if (pass.length >= 8) strength++
    if (/[A-Z]/.test(pass)) strength++
    if (/[0-9]/.test(pass)) strength++
    if (/[^A-Za-z0-9]/.test(pass)) strength++

    const levels = [
      { level: 1, text: 'Debil', color: 'bg-red-500' },
      { level: 2, text: 'Regular', color: 'bg-amber-500' },
      { level: 3, text: 'Buena', color: 'bg-emerald-500' },
      { level: 4, text: 'Fuerte', color: 'bg-emerald-600' },
    ]
    return levels[strength - 1] || { level: 0, text: '', color: '' }
  }

  const passwordStrength = getPasswordStrength(password.new)

  return (
    <div className="space-y-6 animate-pageEnter">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Configuracion</h1>
        <p className="text-gray-500">Administra tu cuenta y preferencias del sistema</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Tabs */}
        <div className="border-b border-gray-100">
          <nav className="flex overflow-x-auto">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-6 py-4 border-b-2 transition-all whitespace-nowrap ${
                  activeTab === id
                    ? 'border-emerald-500 text-emerald-600 bg-emerald-50/50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon size={18} />
                {label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Perfil Tab */}
          {activeTab === 'perfil' && (
            <div className="space-y-6">
              {/* Avatar section */}
              <div className="flex items-center gap-6 p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl">
                <div className="relative">
                  <div className="w-24 h-24 bg-emerald-600 rounded-full flex items-center justify-center text-white text-3xl font-bold">
                    {perfil.nombre.charAt(0)}
                  </div>
                  <button className="absolute bottom-0 right-0 p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors">
                    <Camera className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">{perfil.nombre}</h2>
                  <p className="text-gray-500">{perfil.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                      {perfil.rol}
                    </span>
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                      {perfil.hospital}
                    </span>
                  </div>
                </div>
              </div>

              {/* Form fields */}
              <div className="grid md:grid-cols-2 gap-6">
                <InputField
                  label="Nombre completo"
                  value={perfil.nombre}
                  onChange={(v) => setPerfil({ ...perfil, nombre: v })}
                  placeholder="Tu nombre"
                  icon={User}
                />
                <InputField
                  label="Correo electronico"
                  type="email"
                  value={perfil.email}
                  onChange={(v) => setPerfil({ ...perfil, email: v })}
                  placeholder="tu@email.com"
                  icon={Mail}
                />
                <InputField
                  label="Telefono"
                  value={perfil.telefono}
                  onChange={(v) => setPerfil({ ...perfil, telefono: v })}
                  placeholder="55-1234-5678"
                  icon={Phone}
                />
                <InputField
                  label="Hospital asignado"
                  value={perfil.hospital}
                  onChange={() => {}}
                  icon={Building2}
                  disabled
                />
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={handleSavePerfil}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-sm"
                >
                  {loading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <Save className="w-5 h-5" />
                  )}
                  Guardar cambios
                </button>
              </div>
            </div>
          )}

          {/* Seguridad Tab */}
          {activeTab === 'seguridad' && (
            <div className="space-y-6">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Ultimo acceso</p>
                  <p className="text-sm text-amber-700">Hoy a las 10:30 AM desde 192.168.1.100</p>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800">Cambiar contrasena</h3>

                <div className="space-y-4 max-w-md">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contrasena actual</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={password.current}
                        onChange={(e) => setPassword({ ...password, current: e.target.value })}
                        className="w-full pl-10 pr-12 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        placeholder="********"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nueva contrasena</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={password.new}
                        onChange={(e) => setPassword({ ...password, new: e.target.value })}
                        className="w-full pl-10 pr-12 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        placeholder="********"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {password.new && (
                      <div className="mt-2">
                        <div className="flex gap-1 mb-1">
                          {[1, 2, 3, 4].map((level) => (
                            <div
                              key={level}
                              className={`h-1 flex-1 rounded ${
                                level <= passwordStrength.level ? passwordStrength.color : 'bg-gray-200'
                              }`}
                            />
                          ))}
                        </div>
                        <p className="text-xs text-gray-500">Seguridad: {passwordStrength.text}</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Confirmar contrasena</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="password"
                        value={password.confirm}
                        onChange={(e) => setPassword({ ...password, confirm: e.target.value })}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        placeholder="********"
                      />
                      {password.confirm && password.new === password.confirm && (
                        <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500" />
                      )}
                    </div>
                  </div>

                  <button
                    onClick={handleChangePassword}
                    disabled={loading || !password.current || !password.new || !password.confirm}
                    className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Shield className="w-5 h-5" />
                    Cambiar contrasena
                  </button>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-6 space-y-4">
                <h3 className="font-semibold text-gray-800">Sesiones activas</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <Globe className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">Chrome - macOS</p>
                        <p className="text-sm text-gray-500">Ciudad de Mexico - Sesion actual</p>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                      Activa
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notificaciones Tab */}
          {activeTab === 'notificaciones' && (
            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800">Canales de notificacion</h3>
                <div className="space-y-3">
                  <SettingItem
                    icon={Mail}
                    title="Notificaciones por email"
                    description="Recibir alertas y actualizaciones por correo electronico"
                  >
                    <Toggle
                      checked={notificaciones.email}
                      onChange={(v) => setNotificaciones({ ...notificaciones, email: v })}
                    />
                  </SettingItem>
                  <SettingItem
                    icon={Bell}
                    title="Notificaciones push"
                    description="Alertas en tiempo real en el navegador"
                  >
                    <Toggle
                      checked={notificaciones.push}
                      onChange={(v) => setNotificaciones({ ...notificaciones, push: v })}
                    />
                  </SettingItem>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-6 space-y-4">
                <h3 className="font-semibold text-gray-800">Tipos de alertas</h3>
                <div className="space-y-3">
                  <SettingItem
                    icon={AlertTriangle}
                    title="Alertas urgentes"
                    description="Emergencias y situaciones criticas de quirofanos"
                  >
                    <Toggle
                      checked={notificaciones.urgentes}
                      onChange={(v) => setNotificaciones({ ...notificaciones, urgentes: v })}
                    />
                  </SettingItem>
                  <SettingItem
                    icon={Clock}
                    title="Diferimientos"
                    description="Cirugias reprogramadas o canceladas"
                  >
                    <Toggle
                      checked={notificaciones.diferimientos}
                      onChange={(v) => setNotificaciones({ ...notificaciones, diferimientos: v })}
                    />
                  </SettingItem>
                  <SettingItem
                    icon={Building2}
                    title="Ocupacion de hospitales"
                    description="Alertas cuando la ocupacion supere el 85%"
                  >
                    <Toggle
                      checked={notificaciones.ocupacion}
                      onChange={(v) => setNotificaciones({ ...notificaciones, ocupacion: v })}
                    />
                  </SettingItem>
                  <SettingItem
                    icon={Database}
                    title="Reportes programados"
                    description="Notificaciones de reportes automaticos"
                  >
                    <Toggle
                      checked={notificaciones.reportes}
                      onChange={(v) => setNotificaciones({ ...notificaciones, reportes: v })}
                    />
                  </SettingItem>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={handleSaveNotificaciones}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-sm"
                >
                  <Save className="w-5 h-5" />
                  Guardar preferencias
                </button>
              </div>
            </div>
          )}

          {/* Preferencias Tab */}
          {activeTab === 'preferencias' && (
            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800">Apariencia</h3>
                <SettingItem
                  icon={preferencias.tema === 'light' ? Sun : Moon}
                  title="Tema de la interfaz"
                  description="Selecciona el modo claro u oscuro"
                >
                  <select
                    value={preferencias.tema}
                    onChange={(e) => setPreferencias({ ...preferencias, tema: e.target.value })}
                    className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    <option value="light">Claro</option>
                    <option value="dark">Oscuro</option>
                    <option value="system">Sistema</option>
                  </select>
                </SettingItem>
              </div>

              <div className="border-t border-gray-100 pt-6 space-y-4">
                <h3 className="font-semibold text-gray-800">Regional</h3>
                <div className="space-y-3">
                  <SettingItem
                    icon={Globe}
                    title="Idioma"
                    description="Idioma de la interfaz"
                  >
                    <select
                      value={preferencias.idioma}
                      onChange={(e) => setPreferencias({ ...preferencias, idioma: e.target.value })}
                      className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      <option value="es">Espanol</option>
                      <option value="en">English</option>
                    </select>
                  </SettingItem>
                  <SettingItem
                    icon={Clock}
                    title="Zona horaria"
                    description="Tu zona horaria local"
                  >
                    <select
                      value={preferencias.zonaHoraria}
                      onChange={(e) => setPreferencias({ ...preferencias, zonaHoraria: e.target.value })}
                      className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      <option value="America/Mexico_City">Ciudad de Mexico (GMT-6)</option>
                      <option value="America/Tijuana">Tijuana (GMT-8)</option>
                      <option value="America/Cancun">Cancun (GMT-5)</option>
                    </select>
                  </SettingItem>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-6 space-y-4">
                <h3 className="font-semibold text-gray-800">Datos y privacidad</h3>
                <div className="space-y-3">
                  <SettingItem
                    icon={Download}
                    title="Exportar mis datos"
                    description="Descarga una copia de tu informacion personal"
                  >
                    <button
                      onClick={handleExportData}
                      className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                    >
                      Exportar
                    </button>
                  </SettingItem>
                  <SettingItem
                    icon={Trash2}
                    title="Eliminar cuenta"
                    description="Eliminar permanentemente tu cuenta y todos tus datos"
                    danger
                  >
                    <button
                      onClick={handleDeleteAccount}
                      className="px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      Eliminar
                    </button>
                  </SettingItem>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Version info */}
      <div className="text-center text-sm text-gray-400">
        <p>Sistema de Gestion de Quirofanos Centinela Salud v2.0</p>
        <p>Ultima actualizacion: Diciembre 2024</p>
      </div>
    </div>
  )
}
